import {
  faCheck,
  faMinusCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useRef, useEffect } from "react";

import { Input } from "../../../common/inputs/Input";
import {
  AccountTypeDropdown,
  RoleDropdown,
} from "../../TeamManagement/EditExistingTeamMembers";

const AddUsersTable = ({
  users = [],
  isExisting,
  setUsers,
  updateUsersAndBillingText,
  error,
  isPartOfOrganization,
  orgIsManualBilling,
  hasBillingSetup,
}) => {
  const originalAccountTypesRef = useRef(
    users.reduce((acc, user) => {
      acc[user.email] = user.accountType;
      return acc;
    }, {}),
  );

  useEffect(() => {
    if (setUsers) {
      setUsers(users);
    }
  }, [users, setUsers]);

  const handleInputChange = (e, index, field) => {
    const value = e?.target?.value;
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = {
        ...newValues[index],
        [field]: value,
      };
      return newValues;
    });
    updateUsersAndBillingText();
  };

  const handleCheckboxChange = (e, index) => {
    const checked = e.target.checked;
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = {
        ...newValues[index],
        isAdmin: checked,
      };
      return newValues;
    });
  };

  const handleRemove = (index) => {
    setUsers((prevValues) => {
      const newValues = [...prevValues];
      newValues.splice(index, 1);
      return newValues;
    });
  };

  const columnWidthSmall = !orgIsManualBilling ? "w-1/5" : "w-1/4";
  const columnWidthLarge = !orgIsManualBilling ? "w-1/4" : "w-1/3";

  return (
    <table className="table-fixed w-full dark:text-gray-300 text-left">
      <thead>
        <tr className="border-b dark:border-gray-700/50">
          <th className={`font-semibold text-sm ${columnWidthLarge}`}>
            Email
          </th>
          {isPartOfOrganization && (
            <>
              <th
                className={`font-semibold text-sm ${columnWidthLarge}`}
              >
                First Name
              </th>
              <th
                className={`font-semibold text-sm ${columnWidthLarge}`}
              >
                Last Name
              </th>
            </>
          )}
          <th className={`font-semibold text-sm ${columnWidthLarge}`}>
            Role
          </th>

          {!orgIsManualBilling && (
            <th
              className={`font-semibold text-sm ${columnWidthSmall}`}
            >
              {!hasBillingSetup ? (
                <Tooltip
                  arrow
                  placement="right"
                  title={
                    "Set up billing to modify the account plan of your users"
                  }
                >
                  <div>Account Plan</div>
                </Tooltip>
              ) : (
                <>Account Plan</>
              )}
            </th>
          )}
          <th
            className={`font-semibold text-sm ${columnWidthSmall} text-center`}
          >
            Team Admin
          </th>
          <th className="w-[30px]"></th>
        </tr>
      </thead>
      <tbody>
        {users?.map((user, index) => (
          <tr key={index}>
            <td className={`${columnWidthLarge} px-1 py-1  min-w-0`}>
              <p className="w-full pr-2 truncate overflow-hidden text-ellipsis text-sm bg-transparent text-gray-900 dark:text-gray-300">
                {user?.email ?? ""}
              </p>
            </td>
            {isPartOfOrganization && (
              <>
                <td
                  className={`${columnWidthLarge} px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
                >
                  {isExisting ? (
                    <p className="w-full text-sm bg-transparent text-gray-900 dark:text-gray-300">
                      {user?.firstName ?? ""}
                    </p>
                  ) : (
                    <input
                      type="text"
                      defaultValue={user?.firstName ?? ""}
                      data-testid="first-name-input"
                      className={`w-full bg-white dark:bg-gray-700 border ${
                        error && !users[index]?.firstName
                          ? "border-red-500"
                          : "border-none"
                      } text-gray-900 dark:text-gray-300 pl-2 rounded-md`}
                      onChange={(e) =>
                        handleInputChange(e, index, "firstName")
                      }
                    />
                  )}
                </td>
                <td
                  className={`${columnWidthLarge} px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
                >
                  {isExisting ? (
                    <p className="w-full text-sm bg-transparent text-gray-900 dark:text-gray-300">
                      {user?.lastName ?? ""}
                    </p>
                  ) : (
                    <Input
                      type="text"
                      defaultValue={user?.lastName ?? ""}
                      data-testid="last-name-input"
                      className={`w-full bg-white dark:bg-gray-700 border ${
                        error && !users[index]?.lastName
                          ? "border-red-500"
                          : "border-none"
                      } text-gray-900 dark:text-gray-300 pl-2 rounded-md`}
                      onChange={(e) =>
                        handleInputChange(e, index, "lastName")
                      }
                    />
                  )}
                </td>
              </>
            )}
            <td
              className={`${columnWidthSmall} relative px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
            >
              <RoleDropdown
                user={users[index]}
                onChange={(_, value) =>
                  handleInputChange(
                    { target: { value } },
                    index,
                    "roleId",
                  )
                }
                error={error}
              />
            </td>
            {!orgIsManualBilling && (
              <td
                className={`${columnWidthSmall} relative px-1 py-1 truncate overflow-hidden text-ellipsis min-w-0`}
              >
                <Tooltip
                  arrow
                  placement="right"
                  title={
                    originalAccountTypesRef.current[
                      users[index]?.email
                    ] === "unlimited"
                      ? "You can't modify the plan of a user who is paying for themself"
                      : "Set up billing to modify the account plan of your users"
                  }
                  disableFocusListener={
                    originalAccountTypesRef.current[
                      users[index]?.email
                    ] === "basic" && hasBillingSetup
                  }
                  disableHoverListener={
                    originalAccountTypesRef.current[
                      users[index]?.email
                    ] === "basic" && hasBillingSetup
                  }
                  disableTouchListener={
                    originalAccountTypesRef.current[
                      users[index]?.email
                    ] === "basic" && hasBillingSetup
                  }
                >
                  <div>
                    <AccountTypeDropdown
                      user={users[index]}
                      onChange={(_, value) =>
                        handleInputChange(
                          { target: { value } },
                          index,
                          "accountType",
                        )
                      }
                      disabled={
                        originalAccountTypesRef.current[
                          users[index]?.email
                        ] === "unlimited" || !hasBillingSetup
                      }
                    />
                  </div>
                </Tooltip>
              </td>
            )}
            <td className={`${columnWidthSmall} text-center px-1`}>
              <div className="flex flex-row items-center justify-center relative">
                <input
                  checked={users[index]?.isAdmin || false}
                  onChange={(e) => handleCheckboxChange(e, index)}
                  type="checkbox"
                  className="appearance-none rounded-md bg-white dark:bg-gray-700 border-2 border-indigo-500 checked:bg-indigo-500 dark:checked:bg-indigo-500 checked:border-transparent h-6 w-6 cursor-pointer transition-all"
                  data-testid="team-admin-checkbox"
                />
                {users[index]?.isAdmin && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-white absolute pointer-events-none"
                  />
                )}
              </div>
            </td>
            <td className="w-1/20 text-center px-1">
              <FontAwesomeIcon
                icon={faMinusCircle}
                size="lg"
                className="text-red-500 hover:text-red-700 cursor-pointer"
                onClick={() => handleRemove(index)}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AddUsersTable;
